import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { CallEntity } from './call.entity';
import { UserEntity } from './user.entity';

/**
 * One row per rep-correction event on caller-ID match. Written when
 * a rep clicks "Answer as X" on the call pop's alternatives list
 * (i.e. they picked a different record than the matcher's best
 * suggestion). Unused by the matcher itself today; future ML pass
 * can use these as training signal — "the rep overrode our
 * recency-ranking call this many times for this number," "this
 * caller is consistently re-attributed to vendor X even though we
 * pick client Y first," etc.
 *
 * Captures both the matcher's suggestion AND the rep's choice so we
 * can compute the override rate without re-deriving the matcher's
 * decision after the fact (recency rollups change over time).
 *
 * Attribution events stay even after the call row is soft-deleted —
 * onDelete: 'NO ACTION' on the call FK keeps the audit trail.
 */
@Index('softphone_caller_id_attribution_call_idx', ['fkCallId'])
@Index('softphone_caller_id_attribution_user_idx', ['fkUserId'])
@Index('softphone_caller_id_attribution_chosen_idx', [
	'chosenMatchType',
	'chosenEntityId',
])
@Entity('softphone_caller_id_attribution')
export class SoftphoneCallerIdAttributionEntity extends BaseEntity {
	@Column('int', { name: 'fk_call__id', unsigned: true })
	public fkCallId!: number;

	@Column('int', { name: '_fk_user__id', unsigned: true })
	public fkUserId!: number;

	/** What the matcher's `best` was at routing time. Null when the
	 *  matcher returned no match (the rep picked an alternative the
	 *  matcher didn't even surface — possible via a future "search
	 *  for a different record" affordance). */
	@Column('varchar', {
		name: 'matcherSuggestedType',
		nullable: true,
		length: 16,
	})
	public matcherSuggestedType!: 'client' | 'vendor' | null;

	@Column('int', {
		name: 'matcherSuggestedEntityId',
		nullable: true,
		unsigned: true,
	})
	public matcherSuggestedEntityId!: number | null;

	/** What the rep ended up answering as. */
	@Column('varchar', { name: 'chosenMatchType', length: 16 })
	public chosenMatchType!: 'client' | 'vendor';

	@Column('int', { name: 'chosenEntityId', unsigned: true })
	public chosenEntityId!: number;

	/** Where the rep made the choice — `'call-pop'` for inbound
	 *  Answer-as, `'wrap-up'` for the post-call re-attribute (when
	 *  that affordance lands). */
	@Column('varchar', { name: 'source', length: 32, default: 'call-pop' })
	public source!: string;

	@ManyToOne(() => CallEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_call__id', referencedColumnName: 'id' }])
	public fkCall!: CallEntity;

	@ManyToOne(() => UserEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;
}
